import { toOfficialCategoryName } from '@/utils/edtechCategoryNames';
import { canonicalizeSentiment, getDecisionWindowBandForClosedDeal } from '@/utils/procurementDealUtils';
import { getDealCategories } from '@/utils/procurementCategoryUtils';
import { getDealDisplayValueUSD, getDealValueUSDForTotals } from '@/utils/procurementValueUsd';
import { filterVisibleDeals } from '@/lib/procurementVisibility';

const SENTIMENT_KEYS = ['negative', 'neutral', 'positive', 'unknown'];

function emptySentimentMap() {
  return { negative: 0, neutral: 0, positive: 0, unknown: 0 };
}

function mergeSentimentMaps(a = {}, b = {}) {
  const out = emptySentimentMap();
  for (const k of SENTIMENT_KEYS) {
    out[k] = (a[k] || 0) + (b[k] || 0);
  }
  return out;
}

/** Merge API cells by official category + bucket; enrich deals for drawer. */
export function normalizeDisplacementCells(apiCells = []) {
  const merged = new Map();

  for (const cell of apiCells) {
    const category = toOfficialCategoryName(cell.category) || cell.category || 'Other';
    const bucket = cell.bucket;
    const key = `${category}|${bucket}`;

    const rawDeals = (cell.deals || []).map((d) => {
      const sent = canonicalizeSentiment(d.incumbent_sentiment ?? '');
      return {
        ...d,
        valueUSD: getDealDisplayValueUSD(d),
        sentiment: sent,
        rationale: d.incumbent_sentiment_rationale ?? '',
      };
    });
    const hidden = rawDeals.filter((d) => d?.is_visible === false);
    const deals = rawDeals.filter((d) => d?.is_visible !== false);
    const hiddenValue = hidden.reduce((sum, d) => sum + (Number(d.valueUSD) || 0), 0);
    const dealCount = Math.max(0, (cell.deal_count ?? rawDeals.length) - hidden.length);
    const totalValue = Math.max(0, (cell.total_value_usd ?? 0) - hiddenValue);

    if (!merged.has(key)) {
      merged.set(key, {
        category,
        bucket,
        deal_count: dealCount,
        total_value_usd: totalValue,
        negative_count: cell.negative_count ?? 0,
        sentiment_value: { ...emptySentimentMap(), ...(cell.sentiment_value || {}) },
        sentiment_count: { ...emptySentimentMap(), ...(cell.sentiment_count || {}) },
        deals,
      });
      continue;
    }

    const prev = merged.get(key);
    prev.deal_count += dealCount;
    prev.total_value_usd += totalValue;
    prev.negative_count += cell.negative_count ?? 0;
    prev.sentiment_value = mergeSentimentMaps(prev.sentiment_value, cell.sentiment_value);
    prev.sentiment_count = mergeSentimentMaps(prev.sentiment_count, cell.sentiment_count);
    prev.deals = prev.deals.concat(deals);
  }

  return [...merged.values()];
}

function bandToBucket(band) {
  if (band === '0–6 months') return '0–6';
  if (band === '6–12 months') return '6–12';
  if (band === '12–24 months') return '12–24';
  if (band === '24+ months') return '24+';
  if (band === 'Unknown' || band === 'Expired') return '24+';
  return null;
}

/**
 * Build displacement mosaic cells from closed deals (client path).
 * Used when insight tabs must show tenant-owned closed_deals only.
 */
export function buildDisplacementCellsFromDeals(deals = [], selectedCategories = []) {
  const selected = new Set(
    (selectedCategories || [])
      .map((c) => toOfficialCategoryName(c) || c)
      .filter(Boolean),
  );
  const seenInCategory = new Map();
  const byCatBucket = new Map();

  for (const d of filterVisibleDeals(deals)) {
    const id = (d.deal_id ?? d.id ?? '').toString().trim();
    const dealKey =
      id ||
      `${(d.account_name || d.institution_name || '').trim()}|${(d.country || '').trim()}|${(d.supplier || '').trim()}`;

    const { band } = getDecisionWindowBandForClosedDeal(d);
    const bucket = bandToBucket(band);
    if (!bucket) continue;

    const rawCats = getDealCategories(d);
    const cats = [...new Set(
      (rawCats.length ? rawCats : [d.category || 'Other']).map(
        (c) => toOfficialCategoryName(c) || c,
      ).filter(Boolean),
    )].filter((cat) => selected.size === 0 || selected.has(cat));

    const sent = canonicalizeSentiment(d.incumbent_sentiment ?? d.account?.incumbent_sentiment ?? '');
    const sentKey = SENTIMENT_KEYS.includes(sent) ? sent : 'unknown';
    const valueUSD = getDealValueUSDForTotals(d) || 0;

    for (const cat of cats) {
      let seen = seenInCategory.get(cat);
      if (!seen) {
        seen = new Set();
        seenInCategory.set(cat, seen);
      }
      if (seen.has(dealKey)) continue;
      seen.add(dealKey);

      const cellKey = `${cat}|${bucket}`;
      if (!byCatBucket.has(cellKey)) {
        byCatBucket.set(cellKey, {
          category: cat,
          bucket,
          deal_count: 0,
          total_value_usd: 0,
          negative_count: 0,
          sentiment_value: emptySentimentMap(),
          sentiment_count: emptySentimentMap(),
          deals: [],
        });
      }

      const cell = byCatBucket.get(cellKey);
      cell.deal_count += 1;
      cell.total_value_usd += valueUSD;
      cell.sentiment_value[sentKey] += valueUSD;
      cell.sentiment_count[sentKey] += 1;
      if (sent === 'negative') cell.negative_count += 1;
      cell.deals.push({
        ...d,
        valueUSD,
        sentiment: sent,
        rationale: d.incumbent_sentiment_rationale ?? d.account?.incumbent_sentiment_rationale ?? '',
      });
    }
  }

  return [...byCatBucket.values()];
}
